import { useQuery } from "@tanstack/react-query";
import { api } from "./client";

export type Gym = {
  id: string;
  slug: "brest" | "landerneau";
  name: string;
  address: string;
  phone: string;
  email?: string | null;
  imageUrl?: string | null;
  openingHours: Record<string, [string, string][]>;
  reservationUrl: string;
  reservationEmbedHtml?: string | null;
  latitude?: number | null;
  longitude?: number | null;
};

export type Coach = {
  id: string;
  name: string;
  role: string;
  bio: string;
  photoUrl?: string | null;
  certifications: string[];
  order: number;
  gym?: { slug: string; name: string } | null;
};

export type ClassFormat = {
  id: string;
  code: string;
  name: string;
  duration: number;
  maxCapacity: number;
  description: string;
  color?: string | null;
};

export type ScheduleSlot = {
  id: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  zone: number;
  isClosure: boolean;
  format: ClassFormat | null;
};

export type PricingPlan = {
  id: string;
  category: string;
  name: string;
  priceCents?: number | null;
  period?: string | null;
  highlight: boolean;
  description?: string | null;
  order: number;
};

export type Testimonial = { id: string; author: string; content: string; rating?: number | null };

export const useGyms = () => useQuery({ queryKey: ["gyms"], queryFn: () => api<Gym[]>("/gyms") });

export const useGym = (slug: string) =>
  useQuery({ queryKey: ["gym", slug], queryFn: () => api<Gym>(`/gyms/${slug}`) });

export const useSchedule = (slug: string) =>
  useQuery({
    queryKey: ["schedule", slug],
    queryFn: () => api<ScheduleSlot[]>(`/gyms/${slug}/schedule`),
  });

export const useTeam = () => useQuery({ queryKey: ["team"], queryFn: () => api<Coach[]>("/team") });

export const usePricing = () =>
  useQuery({ queryKey: ["pricing"], queryFn: () => api<PricingPlan[]>("/pricing") });

export const useTestimonials = () =>
  useQuery({ queryKey: ["testimonials"], queryFn: () => api<Testimonial[]>("/testimonials") });

export interface InstagramPost {
  id: string;
  imageUrl: string;
  permalink: string;
  caption: string;
  timestamp: string;
}

export interface InstagramFeedResponse {
  posts: InstagramPost[];
  handle: string | null;
  isMock: boolean;
}

export const useInstagramFeed = (slug: string) =>
  useQuery({
    queryKey: ["instagram", slug],
    queryFn: () => api<InstagramFeedResponse>(`/gyms/${slug}/instagram`),
    staleTime: 1000 * 60 * 30, // 30 min côté client (le backend cache 1h)
  });
